import { Injectable } from "@angular/core";
import { BaseManager } from "./base.manager";
import { LocalStorageRepositoryInterface } from "../../datastore/local/localstorage.interface";
import { PhotosphereRepositoryInterface } from "../../datastore/repositories/photosphere/photosphere.interface";
import { forkJoin, Observable, of } from "rxjs";
import { PhotosphereList } from "../../datastore/entities/photospherelist.model";
import { MarkerType } from "../../crossconcern/types/marker.type";
import { MarkerModel } from "../../datastore/entities/marker.model";
import { PromotionRepositoryInterface } from "../../datastore/repositories/promotion/promotion.interface";
import { PromotionList } from "../../datastore/entities/promotionlist.model";
import { PromotionModel } from "../../datastore/entities/promotion.model";
import { PromotionType } from "../../crossconcern/types/promotion.type";
import { ProductUUIDType } from "../../crossconcern/types/productuuid.type";
import { ProductRepositoryInterface } from "../../datastore/repositories/product/product.interface";
import { concatMap, map } from "rxjs/operators";
import { ProductModel } from "../../datastore/entities/product.model";
import { ProductInfoModel } from "../../datastore/entities/productinfo.model";
import { PhotosphereType } from "../../crossconcern/types/photosphere.type";
import { PhotosphereModel } from "../../datastore/entities/photosphere.model";
import { TranslateService } from "@ngx-translate/core";
import { NurseryUUIDType } from "app/crossconcern/types/nurseryuuid";

@Injectable()
export class PhotosphereManager extends BaseManager {
    constructor(
        protected localStorageRepository: LocalStorageRepositoryInterface,
        private photosphereRepository: PhotosphereRepositoryInterface,
        private promotionRepository: PromotionRepositoryInterface,
        private productRepository: ProductRepositoryInterface,
        private translate: TranslateService) {
        super(localStorageRepository);
    }

    public getPhotospheres(
        pageSize: number,
        page: number,
        nurseryUUID?: string,
        withNursery = false): Observable<PhotosphereList> {
        return this.photosphereRepository.getPhotospheres(pageSize, page, nurseryUUID, withNursery);
    }

    public getPhotosphereMedia(uuid: string) {
        return this.photosphereRepository.getPhotosphereMedia(uuid);
    }

    public updatePhotosphere(uuid: string, photosphere: PhotosphereType): Observable<PhotosphereModel> {
        return this.photosphereRepository.updatePhotosphere(uuid, photosphere);
    }

    public associatePhotosphereProduct(photosphereUuid: string, productUuid: string): Observable<PhotosphereModel> {
        return this.photosphereRepository.associatePhotosphereProduct(photosphereUuid, productUuid);
    }

    public disassociatePhotosphereProducts(photosphereUuid: string): Observable<PhotosphereModel> {
        return this.photosphereRepository.disassociatePhotosphereProducts(photosphereUuid);
    }

    public deletePhotosphere(uuid: string) {
        return this.photosphereRepository.deletePhotosphere(uuid);
    }

    public getMarker(markerUUID: string): Observable<MarkerModel> {
        return this.photosphereRepository.getMarker(markerUUID);
    }

    public addMarker(marker: MarkerType): Observable<MarkerModel> {
        return this.photosphereRepository.addMarker(marker);
    }

    public updateMarker(markerUUID: string, marker: MarkerType): Observable<MarkerModel> {
        return this.photosphereRepository.updateMarker(markerUUID, marker);
    }

    public deleteMarker(markerUUID: string): Observable<{}> {
        return this.photosphereRepository.deleteMarker(markerUUID);
    }

    public getPromotions(pageSize: number, page: number, nurseryUuid: string, featured: boolean): Observable<PromotionList> {
        return this.promotionRepository.getPromotions(pageSize, page, nurseryUuid, featured);
    }

    public getPromotion(uuid: string): Observable<PromotionModel> {
        return this.promotionRepository.getPromotion(uuid);
    }

    public getPromotionMedia(uuid: string) {
        return this.promotionRepository.getPromotionMedia(uuid);
    }

    public addPromotion(promotionType: PromotionType, productUUIDType: ProductUUIDType,
        nurseryUUIDType: NurseryUUIDType, files: FileList[]): Observable<PromotionModel> {
        let createdPromotion = null;
        return this.promotionRepository.addPromotion(promotionType, productUUIDType, nurseryUUIDType)
            .pipe(
                concatMap((promotion) => {
                    createdPromotion = promotion;
                    return this.uploadPromotionImage(promotion.uuid, files);
                }),
                map(() => createdPromotion)
            );
    }

    public addPromotionImage(body: File[], promotionUUID: string): Observable<{}> {
        const formData = new FormData();
        for(const file of body) {
            formData.append("files", file);
        }
        return this.promotionRepository.addPromotionImage(formData, promotionUUID);
    }

    public deletePromotion(uuid: string): Observable<{}> {
        return this.promotionRepository.deletePromotion(uuid);
    }

    public updatePromotion(uuid: string,
        promotionType: PromotionType,
        deletedProducts: ProductUUIDType,
        addedProducts: ProductUUIDType,
        deletedNurseries: NurseryUUIDType,
        addedNurseries: NurseryUUIDType,
        files: FileList[]) {
        return this.promotionRepository.updatePromotion(uuid, promotionType)
            .pipe(
                concatMap(() => this.uploadPromotionImage(uuid, files)),
                concatMap(() => this.updatePromotionProducts(uuid, deletedProducts, addedProducts)),
                concatMap(() => this.updatePromotionNurseries(uuid, deletedNurseries, addedNurseries))

            );
    }

    public updatePromotionProducts(uuid: string, deletedProducts: ProductUUIDType, addedProducts: ProductUUIDType)
        : Observable<{}> {
        const obs = [];
        if(addedProducts.products) {
            obs.push(this.promotionRepository.addPromotionProducts(uuid, addedProducts));
        }
        if(deletedProducts.products) {
            obs.push(this.promotionRepository.deletePromotionProducts(uuid, deletedProducts));
        }
        if(obs) {
            return forkJoin(obs);
        } else {
            return of();
        }
    }

    public updatePromotionNurseries(uuid: string, deletedNurseries: NurseryUUIDType, addedNurseries: NurseryUUIDType)
        : Observable<{}> {
        const obs = [];
        if(addedNurseries.nurseries && addedNurseries.nurseries.length > 0) {
            obs.push(this.promotionRepository.addPromotionNurseries(uuid, addedNurseries));
        }
        if(deletedNurseries.nurseries && deletedNurseries.nurseries.length > 0) {
            obs.push(this.promotionRepository.deletePromotionNurseries(uuid, deletedNurseries));
        }
        if(obs) {
            return forkJoin(obs);
        } else {
            return of();
        }
    }

    public uploadPromotionImage(promotionUuid: string, files: FileList[]): Observable<{}> {
        /* eslint-disable dot-notation */
        if(files !== null && files["image"] && files["image"].length > 0) {
            return this.addPromotionImage(files["image"], promotionUuid);
        }
        /* eslint-enable dot-notation */
        return of({});
    }

    public syncProducts(): Observable<ProductModel[]> {
        return this.productRepository.syncProducts();
    }

    public getProducts(search?: string): Observable<ProductModel[]> {
        return this.productRepository.getProducts(search);
    }

    public getProductInfo(speCod: string, varCod: string): Observable<ProductInfoModel> {
        const currentLang = this.translate.currentLang;
        return this.productRepository.getProductInfo(speCod, varCod, currentLang);
    }
}
